/**
 * Exposes the core API modules.
 * Each module can be an Object or a Getter. For the latter, the di.evaluate
 * function will evaluate the dependencies and get the module.
 * Plugins that expose an API must use PluginManager.registerApiModule
 *
 * @module
 */

var _ = require('optimizely-client-fn');
var generateAnalyticsValue = require('core/plugins/helpers/generate_analytics_value');
var LazyProp = require('./lazy_prop');
var translateServices = require('./translate_services');

var Policy = require('core/utils/policy');
var RedirectUtil = require('core/utils/redirect');

/**
 * @typedef {SafeReference} Audience
 * @property {Conditions} conditions
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {Object} EventFilter
 * @property {string} filterType
 * @property {string} selector
 */

/**
 * @typedef {SafeReference} Event
 * @property {string} apiName
 * @property {string} category
 * @property {Object.<module:api/core_modules~EventFilter>} eventFilter
 * @property {string} eventType
 * @property {string} id
 * @property {string} pageId ID of related Page
 */

/**
 * @typedef {SafeReference} Campaign
 * @property {string} id
 * @property {string} [groupId] Id of the mutually exclusive Group the Campaign is in, if any
 * @property {string} [commitId]
 * @property {number} holdback
 * @property {Object} [integrationSettings]
 * @property {string} policy "random", "ordered", or "equal_priority"
 * @property {Object} [activation]
 * @property {module:api/core_modules~Experiment[]} experiments
 * @property {string[]} pageIds
 * @property {Change[]} [changes]
 * @property {Object} [decisionMetadata]
 */

/**
 * @typedef {SafeReference} Page
 * @property {string} id
 * @property {string} [name]
 * @property {Conditions} [staticConditions]
 * @property {ViewTag[]} [tags]
 * @property {string} [activationType]
 * @property {Function} [activationCode]
 */

/**
 * @typedef {SafeReference} Experiment
 * @property {string} id
 * @property {string} [groupId] Id of the mutually exclusive Group the Experiment is in, if any (if single experiment)
 * @property {string[]} audienceIds
 * @property {module:api/core_modules~Variation[]} variations
 * @property {WeightDistribution[]} [weightDistributions]
 * @property {string} [campaignName] Name of the campaign the experiment is in
 */

/**
 * @typedef {SafeReference} Variation
 * @property {string} id
 * @property {Object.<Action[]>} actions Mapping of pageId to changes
 */

/**
 * @typedef {SafeReference} Group
 * @property {string} id
 * @property {string} [name]
 * @property {WeightDistribution[]} [weightDistributions]
 *
 */

/**
 * @typedef {SafeReference} StaticData
 * @property {Object.<module:api/core_modules~Audience>} audiences
 * @property {Object.<module:api/core_modules~Event>} events
 * @property {Object.<module:api/core_modules~Campaign>} campaigns
 * @property {Object.<module:api/core_modules~Page>} pages
 * @property {Object.<module:api/core_modules~Experiment>} experiments
 * @property {Object.<module:api/core_modules~Variation>} variations
 * @property {Object.<module:api/core_modules~Group>} [groups]
 * @property {string} projectId
 * @property {string} [snippetId]
 * @property {string} accountId
 * @property {string} [dcpServiceId]
 * @property {string} revision
 * @property {string} clientVersion
 */

/**
 * @typedef {Object} VisitorIdState
 * @property {string} [randomId]
 */

/**
 * @typedef {Object} CampaignState
 * @property {string} id
 * @property {string} [campaignName]
 * @property {module:api/core_modules~Audience[]} audiences
 * @property {module:api/core_modules~Experiment} experiment
 * @property {module:api/core_modules~Variation} variation
 * @property {string} reason
 * @property {boolean} visitorRedirected Whether the visitor was redirected in this Campaign
 * @property {boolean} isActive Whether visitor was bucketed in this client activation, or redirected to this page by this Campaign
 * @property {boolean} isInCampaignHoldback Whether the visitor is in the holdback for the Campaign
 */

/**
 * @typedef {module:api/core_modules~CampaignState[]} CampaignStateList
 */

/**
 * @typedef {Object} PageState
 * @property {string} id
 * @property {string} apiName
 * @property {string} category
 * @property {Conditions} [staticConditions]
 * @property {ViewTag[]} tags
 * @property {boolean} isActive
 * @property {Object} metadata
 */

/**
 * @typedef {Object} ExperimentState
 * @property {string} id
 * @property {string} [experimentName]
 * @property {module:api/core_modules~Audience[]} audiences
 * @property {module:api/core_modules~Variation} variation
 * @property {string} reason
 * @property {boolean} visitorRedirected Whether the visitor was redirected in this Experiment
 * @property {boolean} isActive Whether visitor was bucketed in this client activation, or redirected to this page by this Experiment
 * @property {boolean} isInExperimentHoldback Whether the visitor is in the holdback for the Experiment
 */

/**
 * @typedef {Object} VariationMap
 * @property {string} id
 * @property {string} name
 * @property {number} variationIndex The index of the variation in the current list of variations for the variation's experiment
 */

/**
 * @typedef {Object} DecisionObject
 * @property {string} [campaign] - property not included if decision is for a single-experiment layer
 * @property {string} experiment
 * @property {string} variation
 * @property {boolean} [holdback] - property not included if decision is for a single-experiment layer
 */

/**
 * TODO: Include more fields, maybe once all clients are opted into the new redirect system.
 *
 * @typedef {Object} RedirectInfo Information about the current redirect Experiment, if any
 * @property {string} [experimentId] ID of Experiment that user is currently bucketed into, or null if Variation
 * is not found in any Experiments in the currently loaded Campaigns.
 * @property {string} variationId ID of Variation that user is currently bucketed into
 * @property {string} referrer Referrer for page user was redirected from
 */

/**
 * @typedef {Object} CustomAttributeValue
 * @property {string} id
 * @property {string} value
 */

/**
 * Map of provided keys to values
 * @typedef {Object} VisitorProfile
 * @property {module:api/core_modules~CustomAttributeValue} custom
 */

/**
 * Exposes certain static data fields to the get API under 'data'.
 * This is analogous to optimizely.DATA in the old client.
 * @exports data
 * @memberof api/core_modules
 * @type {module:api/core_modules~StaticData}
 * @example
 * ```js
 * var data = optimizely.get('data');
 *
 * // Get Experiment name for Dxperiment '12345'
 * optimizely.get('data').experiments['12345'].name;gt
 *
 * // Get Variation name for Variation '67890'
 * optimizely.get('data').variations['67890'].name;
 *
 * // Get a list of Experiment ids
 * Object.keys(optimizely.get('data').experiments);
 * ```
 */
exports.data = [
  'stores/audience_data',
  'stores/client_metadata',
  'stores/event_data',
  'stores/layer_data',
  'stores/view_data',
  'stores/group_data',
  'stores/interest_group',
  'stores/tag_group',
  'stores/global',
  function getData(AudienceDataStore, ClientMetadataStore, EventDataStore, LayerDataStore, ViewDataStore, GroupDataStore, InterestGroupStore, TagGroupStore, GlobalStore) {
    (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('optimizelyGetData');
    //TODO add dimensiondata - https://optimizely.atlassian.net/browse/CJS-1024
    var campaignMap = {} as any;
    var experimentMap = {} as any;
    var variationMap = {} as any;

    var data:any = {
      audiences: AudienceDataStore.getAudiencesMap(),
      events: EventDataStore.getEventsMap(),
      campaigns: campaignMap,
      pages: ViewDataStore.getPagesMap(),
      experiments: experimentMap,
      variations: variationMap,
      projectId: GlobalStore.getProjectId(),
      snippetId: GlobalStore.getSnippetId(),
      accountId: GlobalStore.getAccountId(),
      dcpServiceId: GlobalStore.getDCPServiceId(),
      revision: GlobalStore.getRevision(),
      clientName: ClientMetadataStore.getClientName(),
      clientVersion: ClientMetadataStore.getClientVersion(),
    };

    var getChange = translateServices.dereferenceChangeId;

    _.each(LayerDataStore.getAll(), function(layer) {
      LazyProp.defineProperty(campaignMap, layer.id, function() {
        var campaign = _.extend({}, layer);
        LazyProp.defineProperty(campaign, 'changes', function() {
          return _.map(layer.changes, getChange);
        }, 'campaign');
        LazyProp.defineProperty(campaign, 'experiments', function() {
          return _.map(layer.experiments, function(experiment) {
            return experimentMap[experiment.id];
          });
        }, 'campaign');
        return campaign;
      }, 'campaignMap', 'byId');

      _.each(layer.experiments, function(experiment) {
        LazyProp.defineProperty(experimentMap, experiment.id, function() {
          var exp = _.extend({}, experiment);
          LazyProp.defineProperty(exp, 'changes', function() {
            return _.map(experiment.changes, getChange);
          }, 'experiment');
          LazyProp.defineProperty(exp, 'variations', function() {
            return _.map(experiment.variations, function(variation) {
              return variationMap[variation.id];
            });
          }, 'experiment');
          return exp;
        }, 'experimentMap', 'byId');

        _.each(experiment.variations, function(variation) {
          LazyProp.defineProperty(variationMap, variation.id, function() {
            var v = _.extend({}, variation);
            LazyProp.defineProperty(v, 'actions', function() {
              return _.map(variation.actions, function(action) {
                return _.extend({}, action, {
                  changes: _.map(action.changes, getChange),
                });
              });
            }, 'variation');
            return v;
          }, 'variationMap', 'byId');
        });
      });
    });

    if (__GROUPS__) {
      data.groups = GroupDataStore.getGroupsMap();
    }

    if (__TARGETING_CONDITIONS__.PREDICTED_INTENT) {
      data.interestGroups = InterestGroupStore.getAll();
      data.tagGroups = TagGroupStore.getAll();
    }

    (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('optimizelyGetData');
    return data;
  },
];

if (__DEPRECATED_SESSION_API__ || __TEST__) {
  /**
   * Returns the state of the session store
   * @exports session
   * @memberof api/core_modules
   * @type {SessionState}
   */
  exports.session = [
    'stores/session',
    function sessions(SessionStore) {
      return SessionStore.getState();
    },
  ];
}

/**
 * Get state of the visitor store
 * @exports visitor
 * @memberof api/core_modules
 * @type {module:api/core_modules~VisitorProfile}
 */
exports.visitor = [
  'stores/visitor',
  function getVisitorProfile(VisitorStore) {
    return _.cloneDeep(VisitorStore.getVisitorProfile());
  },
];

/**
 * Returns the state of the visitor_id store
 * @exports visitor_id
 * @memberof api/core_modules
 * @type {module:api/core_modules~VisitorIdState}
 */
exports.visitor_id = [
  'stores/visitor_id',
  function getVisitorIdStore(VisitorIdStore) {
    return {
      randomId: VisitorIdStore.getRandomId(),
    };
  },
];

/**
 * Get information about the current visitor's state.
 *
 * @memberof api/core_modules
 * @exports state
 */
exports.state = [
  'stores/audience_data',
  'stores/layer_data',
  'stores/layer',
  'stores/view_data',
  'stores/view',
  'stores/global',
  'stores/observed_redirect',
  function(AudienceDataStore, LayerDataStore, LayerStore, ViewDataStore, ViewStore, GlobalStore, ObservedRedirectStore) {
    return {
      /**
       * Returns a map of Campaign states, indexed by Campaign id
       * @param {Object|Function} [filters] property/ies to filter for
       * @returns {Object.<module:api/core_modules~CampaignState>} campaignStates
       * @example
       * ```js
       * var state = window.optimizely.get('state');
       * // Get all active Campaign IDs
       * Object.keys(state.getCampaignStates({isActive: true}));
       *
       * // Get Campaign states with a "reason"
       * state.getCampaignStates(function(campaignState) {
       *   return !!campaignState.reason;
       * });
       * ```
       */
      getCampaignStates: function(filters?:any|Function): Record<string, import('api/core_modules').CampaignState> {
        var campaignStates = {} as any;
        var matchingCampaignStates = getAllMatchingCampaignStates(
          LayerStore, LayerDataStore, ViewStore, AudienceDataStore, GlobalStore, filters, {
            // Since offerConsistency layerStates have a different format, they are only returned by getCampaignStateLists
            includeOfferConsistency: false,
          });

        _.each(matchingCampaignStates, function(campaignState) {
          campaignStates[campaignState.id] = campaignState;
        });

        return campaignStates;
      },
      /**
       * Returns a map of Experiment states in single-experiment layers, indexed by Experiment id.
       * Does not include old experiment states that are no longer in current data.
       * This is intended for single-experiment layers.
       * @param {Object|Function} [filters] property/ies to filter for
       * @returns {Object.<module:api/core_modules~ExperimentState>} experimentStates
       * @example
       * ```js
       * var state = window.optimizely.get('state');
       * // Get all active Experiment IDs
       * Object.keys(state.getExperimentStates({isActive: true}));
       *
       * // Get Experiment states with a "reason"
       * state.getExperimentStates(function(experimentState) {
       *   return !!experimentState.reason;
       * });
       * ```
       */
      getExperimentStates: function(filters?:any|Function): Record<string, import('api/core_modules').ExperimentState> {
        var matchingCampaignStates = getAllMatchingCampaignStates(
          LayerStore, LayerDataStore, ViewStore, AudienceDataStore, GlobalStore, filters, {
            includeOfferConsistency: false,
            onlySingleExperiments: true,
          });

        var whiteList = ['audiences', 'variation', 'reason', 'visitorRedirected', 'isActive'];

        var experimentStates = _.reduce(matchingCampaignStates, function(res, campaignState) {
          var experiment = campaignState.allExperiments[0];

          res[experiment.id] = _.extend({}, _.pick(campaignState, whiteList), {
            id: experiment.id,
            experimentName: experiment.name,
            isInExperimentHoldback: campaignState.isInCampaignHoldback,
          });
          return res;
        }, {});

        return experimentStates;
      },

      /**
       * Returns a map of lists of Campaign states, indexed by Campaign id
       * Only used for offer consistency campaigns, where there may be more than one layerState per layerId
       * @param {Object|Function} [filters] property/ies to filter for
       * @returns {Object.<module:api/core_modules~CampaignStateList>} campaignStates
       * @example
       * ```js
       * var state = window.optimizely.get('state');
       * // Get all active Campaign IDs
       * Object.keys(state.getCampaignStates({isActive: true}));
       *
       * // Get Campaign states with a "reason"
       * state.getCampaignStates(function(campaignState) {
       *   return !!campaignState.reason;
       * });
       * ```
       */
      getCampaignStateLists: function(filters?:any|Function): Record<string, import('api/core_modules').CampaignStateList> {
        var campaignStates = {} as any;
        var matchingCampaignStates = getAllMatchingCampaignStates(
          LayerStore, LayerDataStore, ViewStore, AudienceDataStore, GlobalStore, filters, {
            // Since offerConsistency layerStates have a different format, this API replaces getCampaignStates for offerConsistency campaigns
            includeOfferConsistency: true,
          });

        _.each(matchingCampaignStates, function(campaignState) {
          var id = campaignState.id;
          if (!campaignStates[id]) {
            campaignStates[id] = [];
          }
          campaignStates[id].push(campaignState);
        });

        return campaignStates;
      },

      /**
       * Returns a map of Page states, indexed by Page id
       * @param {Object|Function} [filters] property/ies to filter for
       * @returns {Object.<module:api/core_modules~PageState>} pageStates
       * @example
       * ```js
       * var state = window.optimizely.get('state');
       * // Get all active Page IDs
       * Object.keys(state.getPageStates({isActive: true}));
       *
       * // Get the Page state with Page ID 123
       * state.getPageStates({id: 123})
       * ```
       */
      getPageStates: function(filters?:any|Function): Record<string, import('api/core_modules').PageState> {
        var allViewStates = ViewStore.getAll();
        var augmentedViewStates = _.reduce(allViewStates, function(pageStates, viewState) {
          var viewData = ViewDataStore.get(viewState.id);
          pageStates[viewState.id] = _.extend({},
            _.pick(viewData, ['id', 'name', 'apiName', 'category', 'staticConditions', 'tags']),
            _.pick(viewState, ['isActive', 'metadata']));
          // capture any `null` isActive values as `false`
          pageStates[viewState.id].isActive = !!pageStates[viewState.id].isActive;
          return pageStates;
        }, {});
        return !!filters ? _.pickBy(augmentedViewStates, filters) : augmentedViewStates;
      },

      /**
       * @returns {boolean} true if the visitor is in the global holdback.
       */
      isGlobalHoldback: function(): boolean {
        return GlobalStore.isGlobalHoldback();
      },

      /**
       * @returns {string} current activation ID (GUID)
       */
      getActivationId: function(): string {
        return GlobalStore.getActivationId();
      },

      /**
       * @returns {Object.<module:api/core_modules~VariationMap>} mapping of experimentId --> Variation for each Variation user is currently bucketed into
       */
      getVariationMap: function(): Record<string, import('api/core_modules').VariationMap> {
        var layerStates = LayerStore.getLayerStates();
        var variationMap = {} as any;

        _.each(layerStates, function(layer) {
          var layerData = LayerDataStore.get(layer.layerId);
          if (layer.decision && layer.decision.experimentId) {
            variationMap[layer.decision.experimentId] = {
              id: layer.decision.variationId,
              name: null,
              index: null,
            };
            if (layerData) {
              var selectedExperiment = _.find(layerData.experiments, {id: layer.decision.experimentId});
              if (selectedExperiment && layer.decision.variationId) {
                var selectedVariation = _.find(selectedExperiment.variations, {id: layer.decision.variationId});
                var variationIndex = _.findIndex(selectedExperiment.variations, {id: layer.decision.variationId});
              }
              if (selectedVariation) {
                variationMap[layer.decision.experimentId] = {
                  id: layer.decision.variationId,
                  name: selectedVariation.name,
                  index: variationIndex,
                };
              }
            }
          }
        });
        return variationMap;
      },

      /**
       * @returns {string[]} array of ExperimentIds visitor is bucketed into for which the Campaign was activated in this client pageview
       */
      getActiveExperimentIds: function (): string[] {
        var experimentIds = {} as any;
         _.each(this.getCampaignStateLists({isActive: true}), function(setOfCampaignStates) {
          _.each(setOfCampaignStates, function(campaignState) {
            experimentIds[campaignState.experiment.id] = true;
          });
        });
        return _.keys(experimentIds);
      },

      /**
       * Returns Experiment ID, Variation ID, and effective referrer if visitor was redirected in current Campaign. Otherwise returns null.
       * @returns {module:api/core_modules~RedirectInfo|null}
       */
      getRedirectInfo: function(): import('api/core_modules').RedirectInfo|null {
        var data = RedirectUtil.getExperimentAndVariation();
        if (data) {
          data.referrer = RedirectUtil.getReferrer();
        }
        return data;
      },

      /**
       * @param {Object} config
       * @param {string} config.campaignId The id for the Campaign to get decision data about
       * @param {number} [config.maxLength] The maximum number of characters to include in the output string, default is 255
       * @param {boolean} [config.shouldCleanString] Whether the string should be formatted to remove spaces and other special characters, default is false
       * @returns {string|null} Concatenated string of entity names to represent the decision data, or null if no decision data could be found.
       */
      getDecisionString: function(config:any): string|null {
        if (!config) {
          throw new Error('Must pass a config to getDecisionString');
        }
        config = _.extend({
          maxLength: 255,
          shouldCleanString: false,
        }, config);

        var entities = getDecidedEntitiesBasedOnCampaignId(LayerStore, LayerDataStore, ObservedRedirectStore, config.campaignId);

        if (!entities) {
          return null;
        }

        return generateAnalyticsValue.generateAnalyticsString(entities.layer, entities.experiment, entities.variation, entities.isLayerHoldback, config.maxLength, config.shouldCleanString);
      },

      /**
       * @param {Object} config
       * @param {string} config.campaignId The id for the Campaign to get decision data about
       * @param {number} [config.maxLength] The maximum number of characters to include in each entity's output string, default is 255
       * @param {boolean} [config.shouldCleanString] Whether each entity string should be formatted to remove spaces and other special characters, default is false
       * @returns {module:api/core_modules~DecisionObject|null} object containing entity names that represent the decision data, or null if no decision data could be found.
       */
      getDecisionObject: function(config:any): import('api/core_modules').DecisionObject|null {
        if (!config) {
          throw new Error('Must pass a config to getDecisionObject');
        }
        config = _.extend({
          maxLength: 255,
          shouldCleanString: false,
        }, config);

        var entities = getDecidedEntitiesBasedOnCampaignId(LayerStore, LayerDataStore, ObservedRedirectStore, config.campaignId);

        if (!entities) {
          return null;
        }

        var formattedNamesAndIds = generateAnalyticsValue.formatNamesAndIdsForAnalytics(entities.layer, entities.experiment, entities.variation, config.shouldCleanString);

        var nameStrings = _.mapValues(formattedNamesAndIds.names, function(name, entity) {
          return generateAnalyticsValue.combineAndTruncateIdAndName(name, formattedNamesAndIds.idStrings[entity], config.maxLength);
        });

        var result = {
          experiment: nameStrings.experiment,
          variation: nameStrings.variation,
        };

        if (!Policy.isSingleExperimentPolicy(entities.layer.policy)) {
          _.extend(result, {
            campaign: nameStrings.layer,
            holdback: entities.isLayerHoldback,
          });
        }

        return result;
      }
    };
  },
];

function getDecidedEntitiesBasedOnCampaignId(LayerStore, LayerDataStore, ObservedRedirectStore, campaignId) {
  var layerState = LayerStore.getLayerState(campaignId);
  var layer = LayerDataStore.get(campaignId);
  var redirectData = ObservedRedirectStore.get();
  // TODO: determine what to return if layer is not found in current data store - CJS-1475
  if (!layerState || !layer) {
    if (redirectData) {
      return {
        layer: {
          name: redirectData.layerName,
          id: redirectData.layerId,
          policy: redirectData.layerPolicy,
          integrationStringVersion: redirectData.integrationStringVersion,
        },
        experiment: {
          name: redirectData.experimentName,
          id: redirectData.experimentId,
        },
        variation: {
          name: redirectData.variationName,
          id: redirectData.variationId,
        },
        isLayerHoldback: false,
      };
    }
    return null;
  }

  if (Policy.isSingleExperimentPolicy(layer.policy) && layerState.decision.isLayerHoldback) {
    return null;
  }

  var experimentId = layerState.decision.experimentId;
  var variationId = layerState.decision.variationId;

  if (!experimentId || !variationId) {
    return null;
  }

  var experiment;
  var variation;

  experiment = _.find(layer.experiments, {id: experimentId});

  if (!experiment) {
    return null;
  }

  variation = _.find(experiment.variations, {id: variationId});

  if (!variation) {
    return null;
  }

  return {
    layer: {
      name: layer.name,
      id: layer.id,
      policy: layer.policy,
      integrationStringVersion: layer.integrationStringVersion,
    },
    experiment: {
      name: experiment.name,
      id: experiment.id,
    },
    variation: {
      name: variation.name,
      id: variation.id,
    },
    isLayerHoldback: layerState.decision.isLayerHoldback,
  };
}

/**
 * Gets the current layer states and enriches them with extra properties
 * @param {Object} LayerStore
 * @param {Object} LayerDataStore
 * @param {Object} AudienceDataStore
 * @param {Object} GlobalStore
 * @param {Object|Function|undefined} filters Property/ies to filter for
 * @param {Object} policySettings Campaign-specific settings for how to return data
 * @param {boolean} policySettings.includeOfferConsistency  Whether to include campaigns with offer consistency
 * @param {boolean} policySettings.onlySingleExperiments Whether to filter down to single-experiment campaigns
 * @returns {module:api/core_modules~CampaignState[]}
 * @private
 */
function getAllMatchingCampaignStates(LayerStore:any, LayerDataStore:any, ViewStore:any, AudienceDataStore:any, GlobalStore:any, filters:any|Function|undefined, policySettings:any): import('api/core_modules').CampaignState[] {
  var campaignStates = [];
  var layerStates = LayerStore.getLayerStates();
  // If singleExperiment only, filter the layers before processing
  if (policySettings.onlySingleExperiments) {
    layerStates = _.filter(layerStates, function(layerState) {
      var currentLayerData = LayerDataStore.get(layerState.layerId);
      // currentLayerData undefined so we don't know the policy if it's an old layerState no longer in data
      return currentLayerData && Policy.isSingleExperimentPolicy(currentLayerData.policy);
    });
  }
  // List of active views
  var activeViewIds = ViewStore.getActiveViewIds();
  var updatedLayerStates = _.map(layerStates, function(layerState) {
    var visitorBucketed = !!layerState.decision.variationId;
    var inThisActivation = layerState.decisionActivationId && (layerState.decisionActivationId === GlobalStore.getActivationId());
    var redirectExperimentAndVariationIds = RedirectUtil.getExperimentAndVariation();
    var redirectVariationId = redirectExperimentAndVariationIds ? redirectExperimentAndVariationIds.variationId : null;
    var visitorRedirected = visitorBucketed && layerState.decision.variationId === redirectVariationId;
    var views = LayerDataStore.get(layerState?.layerId)?.viewIds || [];
    var allViewsInactive = _.every(views, viewId => !_.includes(activeViewIds, viewId));
    return _.extend(layerState, {
      isActive: (visitorBucketed && inThisActivation && !allViewsInactive) || visitorRedirected,
      visitorRedirected: visitorRedirected,
    });
  });
  var filteredLayerStates = !!filters ? _.filter(updatedLayerStates, filters) : updatedLayerStates;

  _.each(filteredLayerStates, function(campaignState) {
    var processedLayerState = processLayerState(campaignState, LayerDataStore, AudienceDataStore, policySettings.includeOfferConsistency);
    if (processedLayerState) {
      campaignStates.push(processedLayerState);
    }
  });
  return campaignStates;
}

/**
 * Processes layerState to be returned as a CampaignState through the API
 * @param {Object} layerState
 * @param {Object} LayerDataStore
 * @param {Object} AudienceDataStore
 * @param {boolean} includeOfferConsistency Whether to include campaigns with offer consistency
 * @returns {Object}
 * @private
 */
function processLayerState(layerState:any, LayerDataStore:any, AudienceDataStore:any, includeOfferConsistency:boolean): any {
  var selectedExperiment;
  var selectedVariation;
  var id = layerState.layerId;
  var layerData = LayerDataStore.get(id) || {};
  var allExperiments = _.map(layerData.experiments, function(experiment) {
    return _.pick(experiment, ['id', 'name']);
  });

  // If not including offer consistency, just return if it's an offer consistency layer
  if (!includeOfferConsistency && layerData.decisionMetadata && layerData.decisionMetadata.offerConsistency) {
    return;
  }

  var campaignState:any = {
    id: id,
    campaignName: layerData.name || null,
    experiment: null,
    allExperiments: allExperiments,
    variation: null,
    reason: layerState.decision.reason,
    isActive: !!layerState.isActive, // capture any `null` isActive values as `false`
    visitorRedirected: layerState.visitorRedirected,
    isInCampaignHoldback: layerState.decision.isLayerHoldback,
  };

  // Find the layer's selected experiment, if there is one
  if (layerState.decision && layerState.decision.experimentId) {
    selectedExperiment = _.find(layerData.experiments, {id: layerState.decision.experimentId});
  }
  if (selectedExperiment) {
    campaignState.experiment = _.pick(selectedExperiment, ['id', 'name', 'campaignName']);
  }
  // Find the layer's selected variation, if there is one
  if (selectedExperiment && layerState.decision.variationId) {
    selectedVariation = _.find(selectedExperiment.variations, {id: layerState.decision.variationId});
  }
  if (selectedVariation) {
    campaignState.variation = _.pick(selectedVariation, ['id', 'name']);
  }

  // Get all the audiences visitor was in when layer was activated
  var audiences = _.map(layerState.decisionTicket.audienceIds, function(audienceId) {
    return _.pick(AudienceDataStore.get(audienceId), ['id', 'name']);
  });
  campaignState.audiences = audiences;

  // If offer consistency, add the pageId
  if (layerData.decisionMetadata && layerData.decisionMetadata.offerConsistency) {
    campaignState.pageId = layerState.pageId;
  }

  return campaignState;
}

/**
 *
 * @memberof api/core_modules
 * @exports utils
 */
exports.utils = require('./utils').create();

/**
 * Access jQuery built into the client, or present in window.
 *
 * @memberof api/core_modules
 * @exports jquery
 */
exports.jquery = [
  'env/jquery',
  function(jQuery) {
    return jQuery;
  }
];

// * IMPORTANT: Don't use DI here as registering the EventEmitter creates a circular dependency.
/**
 * Exposes event_emitter so that the client change applier can be created from outside
 * the client and injected with the [event emitter interface](#module_api/public_event_emitter)
 *
 * @memberof api/core_modules
 * @exports event_emitter
 */
exports.event_emitter = require('core/event_emitter/public_event_emitter');

/**
 * Expose the change applier module for the editor.
 */
if (__EDITOR__ || __TEST__) {
  exports['client-change-applier'] = require('../editor/change_applier');
  exports['client-tags'] = require('core/tags');
}
